import React, { useState } from 'react';
import { LeakData } from '../../../types/interfaces';
import { useTranslation } from 'react-i18next';

interface Domain {
  id: number;
  domain_name: string;
}

interface LeakInfoProps {
  selectedDomain: string | null;
  leaksData: LeakData[] | null;
  domains: Domain[];
  onDomainChange: (domain: string) => void;
}

/* ———————————————————————————————————————————————— */
/* Reglas de presentación                               */
/* ———————————————————————————————————————————————— */

// Campos que NUNCA se muestran en las filas
const HIDDEN_FIELDS = new Set(['source_name', 'source_breach_date']);

const LeaksInfo: React.FC<LeakInfoProps> = ({
  selectedDomain,
  leaksData,
  domains,
  onDomainChange,
}) => {
  const { t } = useTranslation();

  /* ------------------ paginación ------------------- */
  const [currentPage, setCurrentPage] = useState(1);
  const dataPerPage = 12;

  const totalItems  = leaksData?.length ?? 0;
  const totalPages  = Math.ceil(totalItems / dataPerPage) || 1;
  const indexLast   = currentPage * dataPerPage;
  const indexFirst  = indexLast - dataPerPage;
  const currentData = (leaksData ?? []).slice(indexFirst, indexLast);

  const goNext  = () => setCurrentPage(p => (p < totalPages ? p + 1 : p));
  const goPrev  = () => setCurrentPage(p => (p > 1 ? p - 1 : p));
  const changeDomain = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onDomainChange(e.target.value);
    setCurrentPage(1);
  };

  /* -------------- efecto “blur” -------------------- */
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const handleMouseDown = (i: number) => setHoveredIndex(i);
  const handleMouseUp   = () => setHoveredIndex(null);

  return (
    <div
      className="bg-white p-6 rounded-lg shadow-lg mb-6 flex flex-col justify-between"
      style={{ boxShadow: '0 4px 10px rgba(169,169,169,.6)', minHeight: '1350px' }}
    >
      <div className="flex-grow">
        {/* ————— Título e introducción ————— */}
        <div className="flex items-center mb-6">
          <div className="w-3 h-10 bg-gray-800 mr-4" />
          <h3 className="text-4xl font-bold">{t('leaksInfo.title')}</h3>
        </div>
        <p className="text-gray-700 mb-4 text-xl">{t('leaksInfo.paragraph1')}</p>
        <p className="text-gray-700 mb-4 text-xl">{t('leaksInfo.paragraph2')}</p>
        <p className="text-gray-700 text-xl">{t('leaksInfo.paragraph3')}</p>

        {/* ————— Selector de dominio ————— */}
        <div className="my-4">
          <label htmlFor="domain-select" className="block text-2xl font-medium text-gray-700 mb-4">
            <strong>{t('leaksInfo.select_domain')}</strong>
          </label>
          <select
            id="domain-select"
            value={selectedDomain || ''}
            onChange={changeDomain}
            className="block w-full max-w-md p-2 border border-gray-800 bg-gray-800 text-white font-bold rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            {domains.map(d => (
              <option key={d.id} value={d.domain_name}>
                {d.domain_name}
              </option>
            ))}
          </select>
        </div>

        {/* ————— Tarjetas con filtraciones ————— */}
        {currentData.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {currentData.map((data, cardIdx) => (
              <div
                key={cardIdx}
                className="border border-gray-300 rounded-lg overflow-hidden flex flex-col"
                onMouseDown={() => handleMouseDown(cardIdx)}
                onMouseUp={handleMouseUp}
              >
                {/* título (fuente) */}
                <div className="bg-gray-800 text-white p-2 font-bold text-center">
                  {/* nombre de la brecha o texto genérico */}
                  {data.source_name || t('leaksInfo.unknown_source')}
                  {/* fecha entre paréntesis, si existe */}
                  {data.source_breach_date && (
                    <>
                      {' '}
                      (<span className="font-normal">{data.source_breach_date}</span>)
                    </>
                  )}
                </div>
                <table className="table-auto w-full border-collapse text-left flex-grow">
                  <tbody>
                    {Object.entries(data)
                      .filter(([k, v]) => v && !HIDDEN_FIELDS.has(k))
                      .map(([k, v]) => (
                        <tr
                          key={k}
                          className="text-center bg-gray-50 even:bg-gray-100 hover:bg-gray-200"
                        >
                          <td className="p-2 font-semibold">{t(`leaksInfo.${k}`)}</td>
                          <td
                            className="p-2 break-words"
                            style={{
                              filter: hoveredIndex === cardIdx ? 'none' : 'blur(5px)',
                              cursor: 'pointer',
                            }}
                          >
                            {v}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center my-8">
            <h3 className="text-2xl font-bold">{t('leaksInfo.no_leaks')}</h3>
          </div>
        )}
      </div>

      {/* ————— Paginación ————— */}
      {totalItems > dataPerPage && (
        <div className="mt-6 flex justify-center items-center space-x-4">
          <button
            onClick={goPrev}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
          >
            {t('leaksInfo.pagination.previous')}
          </button>

          <span>
            {t('leaksInfo.pagination.info', { current: currentPage, total: totalPages })}
          </span>

          <button
            onClick={goNext}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-gray-800 text-white rounded disabled:bg-gray-300 disabled:text-gray-600"
          >
            {t('leaksInfo.pagination.next')}
          </button>
        </div>
      )}
    </div>
  );
};

export default LeaksInfo;
